/**
 * Auth Service
 * Handles authentication-related API calls
 */

import api from "@/lib/api";
import { API_ENDPOINTS } from "@/constants/endpoints";
import { AuthAdapter } from "@/adapters";
import type { ApiResponse, LoginResponseDto, UserAccountsResponseDto } from "@/types";

export const authService = {
  /**
   * Login user
   */
  async login(email: string, password: string) {
    const payload = AuthAdapter.toLoginRequest(email, password);
    const response = await api.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.LOGIN,
      payload,
    );
    return AuthAdapter.fromLoginResponse(response.data.data);
  },

  // Impersonate user (super admin only). accountId optional — backend uses
  // target user's default account when omitted.
  async impersonate(userId: string, accountId?: string) {
    const body: { userId: number; accountId?: number } = { userId: Number(userId) };
    if (accountId) body.accountId = Number(accountId);
    const response = await api.post<ApiResponse<LoginResponseDto>>(API_ENDPOINTS.AUTH.IMPERSONATE, body);
    return AuthAdapter.fromLoginResponse(response.data.data);
  },

  async getUserAccounts() {
    const response = await api.get<ApiResponse<UserAccountsResponseDto>>(
      API_ENDPOINTS.AUTH.USER_ACCOUNTS,
    );
    return AuthAdapter.fromUserAccountsResponse(response.data.data);
  },

  async switchAccount(accountId: number) {
    const payload = AuthAdapter.toSwitchAccountRequest(accountId);
    const response = await api.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.SWITCH_ACCOUNT,
      payload,
    );
    return AuthAdapter.fromLoginResponse(response.data.data);
  },

  /**
   * Request a password-reset email. Backend always returns a generic success
   * message (no account enumeration).
   */
  async forgotPassword(email: string) {
    const payload = { email: email.trim().toLowerCase() };
    const response = await api.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      payload,
      { authRequired: false } as Record<string, unknown>,
    );
    return response.data.data;
  },

  /**
   * Consume a set-password token (invite or forgot link) and set a new password.
   * Returns a login payload so the user is signed in immediately.
   */
  async setPassword(token: string, password: string) {
    const response = await api.post<ApiResponse<LoginResponseDto>>(
      API_ENDPOINTS.AUTH.SET_PASSWORD,
      { token, password },
      { authRequired: false } as Record<string, unknown>,
    );
    return AuthAdapter.fromLoginResponse(response.data.data);
  },

  async ssoLogin(ssoToken: string) {
    const response = await api.get<ApiResponse<LoginResponseDto>>(
      `${API_ENDPOINTS.AUTH.SSO}?sso_token=${encodeURIComponent(ssoToken)}`,
      { authRequired: false } as Record<string, unknown>,
    );
    return AuthAdapter.fromLoginResponse(response.data.data);
  },
};
